'use client'

import {useMemo, useRef, useState} from 'react'
import {Search, Tag, UserPlus, X} from 'lucide-react'
import type {Contact, ContactLabel} from '@/types'
import {displayName, filterContacts} from '@/lib/utils'
import {LazyAvatar} from '@/components/LazyAvatar'
import {parseContactLabelIds} from '@/shared'

type ContactSelectorPanelProps = {
    contacts: Contact[]
    selectedWxids: string[]
    onSelect: (wxid: string) => void
    onRemove: (wxid: string) => void
    onClear?: () => void
    placeholder?: string
    emptyHint?: string
    labels?: ContactLabel[]
    selectedLabelIds?: number[]
    onLabelSelect?: (id: number) => void
    onLabelRemove?: (id: number) => void
    /** Override the root element className (e.g. to remove fixed width in modals) */
    className?: string
}

const BLUR_CLOSE_MS = 200

export function ContactSelectorPanel({
    contacts,
    selectedWxids,
    onSelect,
    onRemove,
    onClear,
    placeholder = '搜索并添加...',
    emptyHint = '请在上方搜索添加好友',
    labels,
    selectedLabelIds,
    onLabelSelect,
    onLabelRemove,
    className,
}: ContactSelectorPanelProps) {
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<'contacts' | 'labels'>('contacts')
    const [labelQuery, setLabelQuery] = useState('')
    const [labelOpen, setLabelOpen] = useState(false)
    const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const labelBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)

    const showLabelTab = !!labels && labels.length > 0 && !!onLabelSelect && !!onLabelRemove

    const selectedSet = useMemo(() => new Set(selectedWxids), [selectedWxids])

    const dropdownContacts = useMemo(() => {
        const base = filterContacts(contacts, query)
        return base.filter((c) => !selectedSet.has(c.wxid))
    }, [contacts, query, selectedSet])

    const selectedContacts = useMemo(() => {
        return selectedWxids.map((wxid) =>
            contacts.find((c) => c.wxid === wxid) || ({wxid, name: wxid} as Contact),
        )
    }, [contacts, selectedWxids])

    const labelMemberCounts = useMemo(() => {
        const map = new Map<number, number>()
        if (!labels) return map
        for (const label of labels) map.set(label.id, 0)
        for (const contact of contacts) {
            for (const id of parseContactLabelIds(contact.labelIds)) {
                if (map.has(id)) map.set(id, (map.get(id) || 0) + 1)
            }
        }
        return map
    }, [contacts, labels])

    const selectedLabelIdSet = useMemo(() => new Set(selectedLabelIds || []), [selectedLabelIds])

    const dropdownLabels = useMemo(() => {
        if (!labels) return []
        let base = labels
        if (labelQuery.trim()) {
            const q = labelQuery.trim().toLowerCase()
            base = base.filter((l) => l.name.toLowerCase().includes(q))
        }
        return base.filter((l) => !selectedLabelIdSet.has(l.id))
    }, [labels, labelQuery, selectedLabelIdSet])

    const validSelectedLabels = useMemo(() => {
        if (!labels || !selectedLabelIds) return []
        const map = new Map(labels.map((l) => [l.id, l]))
        return selectedLabelIds.filter((id) => map.has(id)).map((id) => map.get(id)!)
    }, [labels, selectedLabelIds])

    const hasAnySelection = selectedWxids.length > 0 || validSelectedLabels.length > 0

    function handleSelect(contact: Contact) {
        onSelect(contact.wxid)
        setQuery('')
        setOpen(false)
        inputRef.current?.blur()
    }

    return (
        <section
            role="region"
            aria-label="contact-selector"
            className={className || "w-[300px] bg-white rounded-card shadow-card border border-hair flex flex-col shrink-0 h-full overflow-hidden relative"}
        >
            {/* Tab strip */}
            {showLabelTab && (
                <div className="flex border-b border-hair bg-page shrink-0">
                    <button
                        type="button"
                        className={`flex-1 h-9 text-sm font-medium transition-colors ${
                            activeTab === 'contacts' ? 'text-accent border-b-2 border-accent' : 'text-soft hover:text-ink'
                        }`}
                        onClick={() => setActiveTab('contacts')}
                    >
                        联系人
                    </button>
                    <button
                        type="button"
                        className={`flex-1 h-9 text-sm font-medium transition-colors ${
                            activeTab === 'labels' ? 'text-accent border-b-2 border-accent' : 'text-soft hover:text-ink'
                        }`}
                        onClick={() => setActiveTab('labels')}
                    >
                        标签
                    </button>
                </div>
            )}

            {/* Search area — switches with tab */}
            {(!showLabelTab || activeTab === 'contacts') && (
                <div className="p-3 border-b border-hair bg-page relative z-20">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={placeholder}
                            value={query}
                            aria-expanded={open}
                            onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
                            onFocus={() => { if (blurTimer.current) { clearTimeout(blurTimer.current); blurTimer.current = null }; setOpen(true) }}
                            onBlur={() => { blurTimer.current = setTimeout(() => { setOpen(false); blurTimer.current = null }, BLUR_CLOSE_MS) }}
                            className="w-full h-8 bg-white border border-hair rounded pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                        />
                    </div>
                    {open && (
                        <div role="listbox" className="absolute left-3 right-3 top-full mt-1 bg-white border border-hair rounded-md shadow-card max-h-60 overflow-y-auto z-50">
                            {dropdownContacts.length > 0 ? dropdownContacts.map((contact) => (
                                <div
                                    key={contact.wxid}
                                    role="option"
                                    aria-selected={false}
                                    className="flex items-center p-2.5 hover:bg-page cursor-pointer border-b border-hair/40 last:border-b-0"
                                    onMouseDown={(e) => { e.preventDefault(); handleSelect(contact) }}
                                >
                                    <LazyAvatar src={contact.avatar} seed={contact.wxid} alt={displayName(contact)} className="w-8 h-8 rounded mr-3 bg-gray-100"/>
                                    <div className="flex-1 truncate">
                                        <div className="text-sm font-medium text-ink truncate">{displayName(contact)}</div>
                                        <div className="text-xs text-muted truncate">{contact.wxid}</div>
                                    </div>
                                </div>
                            )) : (
                                <div className="p-3 text-sm text-muted text-center">无匹配或已全部添加</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showLabelTab && activeTab === 'labels' && (
                <div className="p-3 border-b border-hair bg-page relative z-20">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                        <input
                            type="text"
                            placeholder="搜索标签..."
                            value={labelQuery}
                            onChange={(e) => { setLabelQuery(e.target.value); setLabelOpen(true) }}
                            onFocus={() => { if (labelBlurTimer.current) { clearTimeout(labelBlurTimer.current); labelBlurTimer.current = null }; setLabelOpen(true) }}
                            onBlur={() => { labelBlurTimer.current = setTimeout(() => { setLabelOpen(false); labelBlurTimer.current = null }, BLUR_CLOSE_MS) }}
                            className="w-full h-8 bg-white border border-hair rounded pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                        />
                    </div>
                    {labelOpen && (
                        <div role="listbox" className="absolute left-3 right-3 top-full mt-1 bg-white border border-hair rounded-md shadow-card max-h-60 overflow-y-auto z-50">
                            {dropdownLabels.length > 0 ? dropdownLabels.map((label) => {
                                const count = labelMemberCounts.get(label.id) || 0
                                return (
                                    <div
                                        key={label.id}
                                        role="option"
                                        aria-selected={false}
                                        className="flex items-center p-2.5 hover:bg-page cursor-pointer border-b border-hair/40 last:border-b-0"
                                        onMouseDown={(e) => { e.preventDefault(); onLabelSelect?.(label.id); setLabelQuery('') }}
                                    >
                                        <div className="w-8 h-8 rounded mr-3 bg-blue-50 flex items-center justify-center flex-shrink-0">
                                            <Tag size={16} className="text-blue-500"/>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-ink truncate">{label.name}</div>
                                            <div className="text-xs text-muted">共 {count} 人</div>
                                        </div>
                                    </div>
                                )
                            }) : (
                                <div className="p-3 text-sm text-muted text-center">
                                    {labels!.length === 0 ? '暂无标签，请先在「标签管理」中创建' : '无匹配或已全部添加'}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Selected list — ALWAYS visible regardless of active tab */}
            <div className="flex-1 overflow-y-auto p-2 bg-page/50">
                {!hasAnySelection ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted opacity-70 gap-2">
                        <UserPlus size={40}/>
                        <span className="text-sm">{emptyHint}</span>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {validSelectedLabels.map((label) => (
                            <div key={`label-${label.id}`} className="flex items-center justify-between p-2 bg-white rounded border border-hair/70 hover:border-hair transition-colors group">
                                <div className="flex items-center overflow-hidden pr-2">
                                    <div className="w-8 h-8 rounded mr-3 bg-blue-50 flex items-center justify-center flex-shrink-0">
                                        <Tag size={16} className="text-blue-500"/>
                                    </div>
                                    <span className="text-sm text-ink truncate font-medium">{label.name}</span>
                                </div>
                                <button type="button" onClick={() => onLabelRemove?.(label.id)} className="text-muted hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-red-50 rounded" title="移除">
                                    <X size={14}/>
                                </button>
                            </div>
                        ))}
                        {selectedContacts.map((contact) => (
                            <div key={contact.wxid} className="flex items-center justify-between p-2 bg-white rounded border border-hair/70 hover:border-hair transition-colors group">
                                <div className="flex items-center overflow-hidden pr-2">
                                    <LazyAvatar src={contact.avatar} seed={contact.wxid} alt={displayName(contact)} className="w-8 h-8 rounded mr-3 bg-gray-100 flex-shrink-0"/>
                                    <span className="text-sm text-ink truncate font-medium">{displayName(contact)}</span>
                                </div>
                                <button type="button" onClick={() => onRemove(contact.wxid)} className="text-muted hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-red-50 rounded" title="移除">
                                    <X size={14}/>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Clear all */}
            {hasAnySelection && onClear && (
                <div className="px-4 py-2 bg-white border-t border-hair/70 shrink-0 flex justify-end">
                    <button type="button" className="text-xs text-accent hover:text-accent-hover transition-colors" onClick={onClear}>
                        清空全部
                    </button>
                </div>
            )}
        </section>
    )
}
